import { defineStore } from 'pinia'
import { computed, ref } from 'vue'
import type { NotificationInboxDto, NotificationInboxIndexSuccess } from '~/services/api/generated'

type NotificationInboxItem = NotificationInboxDto
type NotificationInboxLinks = NotificationInboxIndexSuccess['links']
type NotificationInboxMeta = NotificationInboxIndexSuccess['meta']

type NotificationConnectionStatus =
  'connected' | 'disconnected' | 'connecting' | 'reconnecting' | 'failed'

export const useNotificationsStore = defineStore('notifications', () => {
  const items = ref<NotificationInboxItem[]>([])
  const unreadCount = ref(0)
  const isLoaded = ref(false)
  const isPanelOpen = ref(false)
  const isConnecting = ref(false)
  const isFetchingMore = ref(false)
  const lastReceivedAt = ref<string | null>(null)
  const error = ref<string | null>(null)
  const connectionStatus = ref<NotificationConnectionStatus>('disconnected')
  const subscribedChannelName = ref<string | null>(null)
  const paginationLinks = ref<Partial<NotificationInboxLinks> | null>(null)
  const paginationMeta = ref<Partial<NotificationInboxMeta> | null>(null)

  const previewItems = computed(() => items.value.slice(0, 5))
  const hasUnread = computed(() => unreadCount.value > 0)
  const hasNextPage = computed(() => Boolean(paginationLinks.value?.next))

  const knownIds = new Set<string>()
  const knownDedupeKeys = new Set<string>()

  const rememberItem = (item: NotificationInboxItem, dedupeKey?: string | null) => {
    knownIds.add(item.id)

    if (dedupeKey !== null && dedupeKey !== undefined && dedupeKey.length > 0) {
      knownDedupeKeys.add(dedupeKey)
    }
  }

  const rebuildKnownState = () => {
    knownIds.clear()
    knownDedupeKeys.clear()

    items.value.forEach((item) => {
      rememberItem(item)
    })
  }

  const hasSeen = (itemId: string, dedupeKey?: string | null) => {
    if (knownIds.has(itemId)) {
      return true
    }

    return (
      dedupeKey !== null &&
      dedupeKey !== undefined &&
      dedupeKey.length > 0 &&
      knownDedupeKeys.has(dedupeKey)
    )
  }

  const replaceItems = (nextItems: NotificationInboxItem[]) => {
    items.value = [...nextItems]
    rebuildKnownState()
  }

  const appendItems = (nextItems: NotificationInboxItem[]) => {
    nextItems.forEach((item) => {
      if (hasSeen(item.id)) {
        return
      }

      items.value.push(item)
      rememberItem(item)
    })
  }

  const prependItem = (item: NotificationInboxItem, dedupeKey?: string | null) => {
    if (hasSeen(item.id, dedupeKey)) {
      return false
    }

    items.value.unshift(item)
    rememberItem(item, dedupeKey)
    lastReceivedAt.value = item.created_at
    return true
  }

  const setUnreadCount = (count: number) => {
    unreadCount.value = Math.max(0, count)
  }

  const incrementUnreadCount = () => {
    unreadCount.value += 1
  }

  const setLoaded = (loaded: boolean) => {
    isLoaded.value = loaded
  }

  const setPanelOpen = (value: boolean) => {
    isPanelOpen.value = value
  }

  const setError = (message: string | null) => {
    error.value = message
  }

  const setConnectionStatus = (status: NotificationConnectionStatus) => {
    connectionStatus.value = status
    isConnecting.value = status === 'connecting' || status === 'reconnecting'
  }

  const setFetchingMore = (value: boolean) => {
    isFetchingMore.value = value
  }

  const setSubscribedChannelName = (channelName: string | null) => {
    subscribedChannelName.value = channelName
  }

  const setPagination = (
    links: Partial<NotificationInboxLinks> | null,
    meta: Partial<NotificationInboxMeta> | null
  ) => {
    paginationLinks.value = links
    paginationMeta.value = meta
  }

  const markAsReadLocally = (notificationId: string) => {
    const item = items.value.find((entry) => entry.id === notificationId)

    if (!item || item.is_read) {
      return false
    }

    item.is_read = true
    item.read_at = new Date().toISOString()
    unreadCount.value = Math.max(0, unreadCount.value - 1)
    return true
  }

  const markAllAsReadLocally = () => {
    items.value = items.value.map((item) => {
      if (item.is_read) {
        return item
      }

      return {
        ...item,
        is_read: true,
        read_at: new Date().toISOString()
      }
    })

    unreadCount.value = 0
  }

  const removeItems = (notificationIds: string[]) => {
    if (!notificationIds.length) {
      return
    }

    const targetIds = new Set(notificationIds)
    items.value = items.value.filter((item) => !targetIds.has(item.id))
    rebuildKnownState()
  }

  const reset = () => {
    items.value = []
    unreadCount.value = 0
    isLoaded.value = false
    isPanelOpen.value = false
    isConnecting.value = false
    isFetchingMore.value = false
    lastReceivedAt.value = null
    error.value = null
    connectionStatus.value = 'disconnected'
    subscribedChannelName.value = null
    paginationLinks.value = null
    paginationMeta.value = null
    knownIds.clear()
    knownDedupeKeys.clear()
  }

  return {
    items,
    unreadCount,
    isLoaded,
    isPanelOpen,
    isConnecting,
    isFetchingMore,
    lastReceivedAt,
    error,
    connectionStatus,
    subscribedChannelName,
    paginationLinks,
    paginationMeta,
    previewItems,
    hasUnread,
    hasNextPage,
    replaceItems,
    appendItems,
    prependItem,
    setUnreadCount,
    incrementUnreadCount,
    setLoaded,
    setPanelOpen,
    setError,
    setConnectionStatus,
    setFetchingMore,
    setSubscribedChannelName,
    setPagination,
    markAsReadLocally,
    markAllAsReadLocally,
    removeItems,
    reset
  }
})
